import { useEffect, useMemo, useState } from "react";
import { SlidersHorizontal, CreditCard } from "lucide-react";
import { t } from "@/i18n/sk";
import { getCards, getExpansions, getEras } from "@/lib/api";
import { CardCard } from "@/components/CardCard";
import { HoloBackground } from "@/components/HoloBackground";

export default function IndividualCards() {
  const [cards, setCards] = useState([]);
  const [expansions, setExpansions] = useState([]);
  const [eras, setEras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [eraFilter, setEraFilter] = useState("all");
  const [expansionFilter, setExpansionFilter] = useState("all");
  const [rarityFilter, setRarityFilter] = useState("all");
  const [sort, setSort] = useState("default");

  useEffect(() => {
    getExpansions().then(setExpansions).catch(() => {});
    getEras().then(setEras).catch(() => {});
    getCards().then(setCards).catch(() => setCards([])).finally(() => setLoading(false));
  }, []);

  const expEraMap = useMemo(() => Object.fromEntries(expansions.map((e) => [e.slug, e.era_slug])), [expansions]);
  const seriesOptions = eraFilter === "all" ? expansions : expansions.filter((e) => e.era_slug === eraFilter);
  const rarities = useMemo(() => [...new Set(cards.map((c) => c.rarity).filter(Boolean))], [cards]);

  const filtered = useMemo(() => {
    let list = [...cards];
    if (eraFilter !== "all") list = list.filter((c) => expEraMap[c.expansion_slug] === eraFilter);
    if (expansionFilter !== "all") list = list.filter((c) => c.expansion_slug === expansionFilter);
    if (rarityFilter !== "all") list = list.filter((c) => c.rarity === rarityFilter);
    if (sort === "price-asc") list.sort((a, b) => (a.price_eur ?? 1e9) - (b.price_eur ?? 1e9));
    if (sort === "price-desc") list.sort((a, b) => (b.price_eur ?? -1) - (a.price_eur ?? -1));
    if (sort === "name") list.sort((a, b) => a.name.localeCompare(b.name));
    return list;
  }, [cards, eraFilter, expansionFilter, rarityFilter, sort, expEraMap]);

  const selectClass = "rounded-lg border border-white/10 bg-vault-card px-3 py-2 text-sm text-white focus:border-cyan-glow/50 focus:outline-none";

  return (
    <div className="vault-bg">
      <section className="relative overflow-hidden border-b border-white/8">
        <HoloBackground />
        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <span className="overline mb-3 block">Singles</span>
          <h1 className="font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl">{t.nav.cards}</h1>
          <p className="mt-4 max-w-2xl text-slate-300">Jednotlivé zberateľské Pokémon TCG karty. Vyber sériu a filtruj podľa rarity.</p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        {cards.length > 0 && (
          <div className="mb-8 flex flex-wrap items-center gap-3">
            <span className="flex items-center gap-2 text-sm font-semibold text-slate-300"><SlidersHorizontal className="h-4 w-4 text-cyan-300" /> {t.catalog.filters}</span>
            {eras.length > 0 && (
              <select data-testid="filter-era" value={eraFilter} onChange={(e) => { setEraFilter(e.target.value); setExpansionFilter("all"); }} className={selectClass}>
                <option value="all">Všetky éry</option>
                {eras.map((er) => <option key={er.slug} value={er.slug}>{er.name}</option>)}
              </select>
            )}
            <select data-testid="filter-expansion" value={expansionFilter} onChange={(e) => setExpansionFilter(e.target.value)} className={selectClass}>
              <option value="all">{t.catalog.allSeries}</option>
              {seriesOptions.map((e) => <option key={e.slug} value={e.slug}>{e.name}</option>)}
            </select>
            {rarities.length > 0 && (
              <select data-testid="filter-rarity" value={rarityFilter} onChange={(e) => setRarityFilter(e.target.value)} className={selectClass}>
                <option value="all">Všetky rarity</option>
                {rarities.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            )}
            <select data-testid="filter-sort" value={sort} onChange={(e) => setSort(e.target.value)} className={selectClass}>
              <option value="default">{t.catalog.sort}</option>
              <option value="price-asc">Cena: najnižšia</option>
              <option value="price-desc">Cena: najvyššia</option>
              <option value="name">Názov A–Z</option>
            </select>
            <span className="ml-auto text-sm text-slate-400">{t.catalog.results(filtered.length)}</span>
          </div>
        )}

        {loading ? (
          <div className="py-20 text-center text-slate-500">Načítavam…</div>
        ) : cards.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-4 rounded-2xl border border-dashed border-white/12 bg-vault-card/50 py-24 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full border border-white/10 bg-vault-black/60"><CreditCard className="h-7 w-7 text-slate-500" /></div>
            <p className="max-w-md text-slate-400">{t.catalog.noCards}</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-5">
            {filtered.map((c, i) => <CardCard key={c.id} card={c} index={i} />)}
          </div>
        )}
      </section>
    </div>
  );
}
